<template>
  <div>
    <div class="card">
      <h5 class="card-header">Interviews</h5>
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th scope="col" class="border-top-0">Name</th>
              <th scope="col" class="border-top-0">Attributes</th>
              <th class="text-right border-top-0 enabled-header">
                Enabled
                <span data-toggle="tooltip" data-placement="top" title="Enables or disables the interview for the organization.">
                  <fa icon="question-circle" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr colspan="12" v-if="!items.length" class="text-muted text-center">
              <td colspan="12">
                No interviews
              </td>
            </tr>
            <tr v-for="item in items" :key="item.id">
              <td>
                <span class="pt-2" :class="item.status !== 'enabled' ? 'text-muted' : ''">{{ item.displayName }}</span>
              </td>
              <td>
                <span v-if="!item.isConfigured"></span>
                <button v-if="item.isConfigured" class="btn btn-sm btn-link px-0" @click="viewAttributes(item)">
                  View Attributes
                </button>
              </td>
              <td class="text-right">
                <div v-if="!item.isConfigured">
                  <span class="pt-2"
                    ><button class="btn btn-sm btn-outline-warning" @click="showModal(item)" title="Complete setup to enable this interview.">
                      <fa icon="exclamation-triangle" /> Complete Setup
                    </button></span
                  >
                </div>
                <div v-if="item.isConfigured" class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="item.id"
                    :disabled="loadingMessage"
                    :checked="item.status === 'enabled'"
                    @change="toggle(item, $event)"
                  />
                  <label class="custom-control-label" :for="item.id"></label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="card-footer">
        <ErrorSummary :error="error" />
        <form ref="form" class="mt-3 needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
          <div class="row">
            <div class="col">
              <div class="form-group">
                <input
                  type="text"
                  class="form-control"
                  id="displayName"
                  placeholder="name"
                  required
                  v-model="displayName"
                  :disabled="loadingMessage"
                  @keyup.enter="submit"
                />
                <div class="invalid-feedback">Please provide a name.</div>
              </div>
            </div>
            <div class="mr-3">
              <button type="button" class="btn btn-primary" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
                <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {{ loadingMessage ? loadingMessage : "Add" }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <!-- Modal (Edit) -->
    <div ref="modalEdit" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modal" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title text-capitalize">Interview Attributes</h5>
            <button type="button" class="close" aria-label="Close" @click="closeModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body p-0">
            <EditOrganizationInterviewAttributes :organizationId="organizationId" :interviewId="modalEditInterviewId" @completed="closeModal" />
          </div>
        </div>
      </div>
    </div>
    <!-- Modal (View) -->
    <div ref="modalView" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modal" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title text-capitalize">Interview Attributes</h5>
            <button type="button" class="close" aria-label="Close" data-dismiss="modal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body p-0">
            <ViewOrganizationInterviewAttributes :organizationId="organizationId" :interviewId="modalViewInterviewId" />
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import ErrorSummary from "@/components/ErrorSummary.vue";
import EditOrganizationInterviewAttributes from "@/views/reports/organizations/EditOrganizationInterviewAttributes.vue";
import ViewOrganizationInterviewAttributes from "@/views/reports/organizations/ViewOrganizationInterviewAttributes.vue";

export default {
  components: {
    ErrorSummary,
    EditOrganizationInterviewAttributes,
    ViewOrganizationInterviewAttributes,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      displayName: null,
      items: [],

      modalEditInterviewId: null,
      modalViewInterviewId: null,
    };
  },
  created() {
    this.load();
  },
  mounted() {
    $('[data-toggle="tooltip"]').tooltip({ boundary: "window" });
  },
  watch: {
    organizationId() {
      this.load();
    },
  },
  methods: {
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/interviews`)
        .then(res => (this.items = res.data))
        .catch(() => this.$router.push("/404"))
        .finally(() => (this.loadingMessage = null));
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;
      this.successMessage = null;
      this.error = null;
      this.displayName = null;
    },
    submit() {
      const payload = {
        displayName: this.displayName,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Adding...";
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/interviews`, payload)
        .then(res => {
          this.showModal(res.data);
        })
        .then(() => {
          this.reset();
          this.load();
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    toggle(item, $event) {
      const payload = {
        displayName: item.displayName,
        status: $event.target.checked ? "enabled" : "disabled",
      };

      this.loadingMessage = "Saving...";
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/interviews/${item.id}`, payload)
        .then(() => {
          this.reset();
          this.load();
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    showModal(interview) {
      this.modalEditInterviewId = interview.id;
      $(this.$refs.modalEdit).modal("show");
    },
    closeModal() {
      this.load();
      $(this.$refs.modalEdit).modal("hide");
    },
    viewAttributes(interview) {
      this.modalViewInterviewId = interview.id;
      $(this.$refs.modalView).modal("show");
    },
  },
};
</script>

<style lang="scss" scoped>
.table-responsive {
  height: 400px;
  overflow-y: scroll;

  table {
    margin-bottom: 0;

    thead tr:nth-child(1) th {
      background: white;
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .enabled-header {
      width: 200px;
    }
  }
}

.custom-control-label {
  cursor: pointer;
}
</style>
